import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import {
  CRM_ETAPA_LABELS,
  CRM_ETAPA_TONES,
  CRM_SCORE_TONES,
  CRM_TIPO_LABELS,
  CRM_TIPO_TONES,
  type EmpresaRow,
} from '../types';
import EmpresaModal from './EmpresaModal';

interface LeadsProps {
  password: string;
  refreshKey: number;
  onChanged: () => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export default function Leads({ password, refreshKey, onChanged }: LeadsProps) {
  const [empresas, setEmpresas] = useState<EmpresaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'publico' | 'privado'>('todos');
  const [modalId, setModalId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'list_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as { empresas?: EmpresaRow[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEmpresas(parsed?.empresas ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return empresas.filter((e) => {
      if (tipoFiltro !== 'todos' && (e.tipo ?? 'privado') !== tipoFiltro) return false;
      if (!q) return true;
      return (
        e.nome.toLowerCase().includes(q) ||
        (e.cidade ?? '').toLowerCase().includes(q) ||
        (e.segmento ?? '').toLowerCase().includes(q) ||
        (e.cnpj ?? '').includes(q)
      );
    });
  }, [empresas, search, tipoFiltro]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-building-4-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Leads</h2>
            <p className="text-sm text-gray-500">{empresas.length} empresas na base</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative max-w-sm w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por empresa, cidade, segmento ou CNPJ..."
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white"
          />
        </div>

        <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1">
          {([
            { key: 'todos', label: 'Todos', icon: 'ri-apps-2-line' },
            { key: 'privado', label: 'Privado', icon: 'ri-building-2-line' },
            { key: 'publico', label: 'Público · Licitações', icon: 'ri-government-line' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => setTipoFiltro(t.key)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                tipoFiltro === t.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={t.icon}></i>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {loading && empresas.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-hop-border">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-building-4-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500">Nenhuma empresa encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtradas.map((e) => {
            const etapa = e.oportunidades?.[0]?.etapa ?? 'novo';
            const scoreTone = CRM_SCORE_TONES[e.classificacao] ?? CRM_SCORE_TONES.baixa;
            return (
              <div
                key={e.id}
                onClick={() => setModalId(e.id)}
                className="bg-white rounded-2xl border border-hop-border p-5 cursor-pointer hover:border-[#E8610A]/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl shrink-0">
                      <i className="ri-building-4-line text-lg"></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{e.nome}</h3>
                      <p className="text-xs text-gray-500 truncate">
                        {[e.cidade, e.segmento].filter(Boolean).join(' · ') || 'Sem cidade'}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${scoreTone.tone}`}>
                    <i className={scoreTone.icon}></i>
                    {e.score}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${CRM_TIPO_TONES[e.tipo === 'publico' ? 'publico' : 'privado'] ?? ''}`}>
                    <i className={e.tipo === 'publico' ? 'ri-government-line' : 'ri-building-2-line'}></i>
                    {CRM_TIPO_LABELS[e.tipo === 'publico' ? 'publico' : 'privado'] ?? 'Privado'}
                  </span>
                  <span className={`inline-flex items-center text-[11px] font-semibold border rounded-full px-2 py-0.5 ${CRM_ETAPA_TONES[etapa] ?? ''}`}>
                    {CRM_ETAPA_LABELS[etapa] ?? etapa}
                  </span>
                  {e.valor_potencial != null && (
                    <span className="text-xs font-semibold text-gray-700">{formatBRL(e.valor_potencial)}</span>
                  )}
                  {e.distancia_km != null && (
                    <span className="text-xs text-gray-400">{e.distancia_km} km</span>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-hop-border flex items-center justify-between text-xs text-gray-500">
                  <span>
                    <i className="ri-contacts-line mr-1"></i>
                    {e.contatos?.length ?? 0} contato{(e.contatos?.length ?? 0) === 1 ? '' : 's'}
                  </span>
                  <span>
                    <i className="ri-time-line mr-1"></i>
                    {formatDate(e.oportunidades?.[0]?.proxima_data ?? null)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalId != null && (
        <EmpresaModal
          empresaId={modalId}
          password={password}
          onClose={() => setModalId(null)}
          onChanged={async () => {
            await load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}